import { IModel } from '@alberta/konexi-shared';
import { Paginated, Params, Query } from '@feathersjs/feathers';
import { IGenericStorage } from 'src/app/shared/services/contracts/database/generic-storage';
import { IQuery } from 'src/app/shared/services/contracts/query/query';
import { IFeathersAppProvider } from 'src/app/shared/services/contracts/sync/feathers-app-provider';

import { RawOptions } from '../contracts/repository/raw-options';
import { AggregateStage } from './aggregate-stage.interface';
import { DbQueryStrategy } from './db-query-strategy';

export class RemoteQueryStrategy<T extends IModel & {}> extends DbQueryStrategy<T> {
  constructor(
    databaseName: string,
    storage: IGenericStorage,
    feathersAppProvider: IFeathersAppProvider,
    user,
    plugins: any
  ) {
    super(databaseName, storage, feathersAppProvider, user, plugins);
  }

  public getAll(): Promise<T[]> {
    return this.fetchFirstPageOfQueryResult(this.getParamsWithAuth(this.user, { query: { $paginate: false } }));
  }

  public getItems(keys: string[]): Promise<any[]> {
    return this.fetchFirstPageOfQueryResult(
      this.getParamsWithAuth(this.user, { query: { _id: { $in: keys }, $paginate: false } })
    );
  }

  public async get(id: string): Promise<T> {
    try {
      const result = await this.feathersAppProvider.app.service(this.table).get(id, this.getParamsWithAuth(this.user));

      return result;
    } catch (e) {
      return void 0;
    }
  }

  public searchInternal(ids: string[], query: IQuery): Promise<T[]> {
    const params = {
      query: {
        _id: { $in: ids },
        archived: this.withArchive.includes(this.table) ? undefined : { $ne: true },
        $paginate: false,
      },
    };

    if (query.ignoreRegionIds) {
      params.query['$ignoreRegionIds'] = true;
    }

    if (query.ignoreMetadataType) {
      params.query['$ignoreMetadataType'] = true;
    }

    return this.fetchFirstPageOfQueryResult(this.getParamsWithAuth(this.user, params));
  }

  public async searchInternalByQuery(
    query: Query,
    aggregate: AggregateStage[],
    ignoreRegionIds: boolean,
    ignoreMetadataType?: boolean,
    sort?: { [key: string]: number }
  ) {
    const params = {
      query: {
        ...query,
        archived: this.withArchive.includes(this.table) ? undefined : { $in: [null, false] },
        $paginate: false,
      },
    };

    if (ignoreRegionIds) {
      params.query['$ignoreRegionIds'] = true;
    }

    if (ignoreMetadataType) {
      params.query['$ignoreMetadataType'] = true;
    }

    if (sort) {
      params.query['$sort'] = sort;
    }

    const paramsWith$client = this.getParamsWithAuth(this.user, params);
    if (aggregate) {
      if (!this.withArchive.includes(this.table)) {
        aggregate.push({ $match: { archived: { $in: [null, false] } } });
      }
      paramsWith$client.query.$client.syncData.aggregate = aggregate;
    }

    return this.fetchFirstPageOfQueryResult(paramsWith$client);
  }

  public async raw({
    aggregation,
    query = {},
    options,
  }: {
    aggregation?: any[];
    query?: any;
    options?: RawOptions;
  }): Promise<any> {
    if (aggregation && query && Object.keys(query).length > 0) {
      throw new Error('Cannot use aggregation and query at the same time');
    }
    if (options?.ignoreRegionIds) {
      query['$ignoreRegionIds'] = true;
    }
    const paramsWith$client = this.getParamsWithAuth(this.user, { query });
    if (aggregation) {
      paramsWith$client.query.$client.syncData.aggregate = aggregation;
    }

    if (!options?.timeout) {
      if (aggregation) {
        return this.fetchAllQueryResults(paramsWith$client);
      }
      return this.fetchFirstPageOfQueryResult(paramsWith$client);
    }

    return new Promise(async (resolve, reject) => {
      const timeoutHandle = setTimeout(() => {
        reject('timeout');
      }, options.timeout);
      let result;
      if (aggregation) {
        result = await this.fetchAllQueryResults(paramsWith$client);
      } else {
        result = await this.fetchFirstPageOfQueryResult(paramsWith$client);
      }
      clearTimeout(timeoutHandle);
      resolve(result);
    });
  }

  private isPaginated(paginated: any[] | Paginated<any>): paginated is Paginated<any> {
    return paginated && (paginated as Paginated<any>).data !== undefined;
  }

  /** Due to a bug in feathersJS pagination is broken if we search with a feathersJS query object.
   * We can only return the first page of paginated results.
   * https://github.com/feathersjs/feathers/issues/3057
   */
  private async fetchFirstPageOfQueryResult(params: Params): Promise<any[]> {
    const service = this.feathersAppProvider.app.service(this.table);
    const queryResult = await service.find(params);

    if (this.isPaginated(queryResult)) {
      return queryResult.data;
    }
    return queryResult;
  }

  /** handles pagination correctly */
  private async fetchAllQueryResults(params: Params): Promise<any[]> {
    const service = this.feathersAppProvider.app.service(this.table);
    const queryResult = await service.find(params);

    if (!queryResult) {
      return [];
    }
    if (!this.isPaginated(queryResult)) {
      return queryResult;
    }

    const totalItemCount = queryResult.total;
    let fetchedItems = queryResult.data;

    const nextPageQueryParams = { ...params };
    while (fetchedItems.length < totalItemCount) {
      nextPageQueryParams.query.$skip = fetchedItems.length;
      const nextPageQueryResult = await service.find(nextPageQueryParams);
      fetchedItems = fetchedItems.concat(nextPageQueryResult.data);
    }
  }
}
